import { useEffect, useLayoutEffect, useRef, useState, type ReactNode, type RefObject } from 'react'
import { Button, Interactive } from '@highlight/ui'
import { motion, type Transition, type Variants } from 'framer-motion'

import { sidepanelVisibleWidth, useSidepanelRenderedWidth, useSidepanelSide } from '../../../atoms'
import { type TourStepDescriptor } from '../build-tour-steps'
import { TOUR_HEROES } from './heroes'
import { TourConnector } from './tour-connector'

/** Horizontal gap between the tour card and the panel's visible inner edge. */
const TOUR_CARD_PANEL_GAP = 16
const CARD_WIDTH = 350
/** Keeps a viewport-clamped card clear of the screen edges. */
const VIEWPORT_MARGIN = 16

const contentVariants: Variants = {
  hidden: { opacity: 0, y: 8 },
  visible: { opacity: 1, y: 0 },
}

const reducedContentVariants: Variants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 },
}

const contentTransition: Transition = { duration: 0.2, ease: [0.32, 0.72, 0, 1] }

interface TourCardProps {
  step: TourStepDescriptor
  stepIndex: number
  totalSteps: number
  /** Viewport Y of the spotlighted target's center; null until measured. */
  anchorY: number | null
  isFirstStep: boolean
  isLastStep: boolean
  prefersReducedMotion: boolean
  onSkip: () => void
  onPrev: () => void
  onNext: () => void
}

/**
 * Splits a paragraph so the summon hotkey can be emphasized without the copy
 * module having to know about React.
 */
function renderParagraph(text: string, emphasis?: string): ReactNode {
  if (!emphasis) return text
  const at = text.indexOf(emphasis)
  if (at === -1) return text
  return (
    <>
      {text.slice(0, at)}
      <strong className="font-semibold">{emphasis}</strong>
      {text.slice(at + emphasis.length)}
    </>
  )
}

/** Card height and viewport height, both of which the vertical clamp depends on. */
function useCardMetrics(cardRef: RefObject<HTMLDivElement | null>) {
  const [cardHeight, setCardHeight] = useState(0)
  const [viewportHeight, setViewportHeight] = useState(() => window.innerHeight)

  // Measured before paint: the vertical clamp needs a real height on the very
  // first frame, or the card visibly slides into place as the tour opens.
  useLayoutEffect(() => {
    const node = cardRef.current
    if (!node) return
    // The card's own height changes with each step's copy, so a mount-time
    // measurement alone would leave later steps mis-centered.
    const observer = new ResizeObserver(([entry]) => setCardHeight(entry.contentRect.height))
    observer.observe(node)
    setCardHeight(node.getBoundingClientRect().height)
    return () => observer.disconnect()
  }, [cardRef])

  useEffect(() => {
    const handleResize = () => setViewportHeight(window.innerHeight)
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  return { cardHeight, viewportHeight }
}

export function TourCard({
  step,
  stepIndex,
  totalSteps,
  anchorY,
  isFirstStep,
  isLastStep,
  prefersReducedMotion,
  onSkip,
  onPrev,
  onNext,
}: TourCardProps) {
  const side = useSidepanelSide()
  const sidepanelWidth = useSidepanelRenderedWidth()
  const cardRef = useRef<HTMLDivElement>(null)
  const { cardHeight, viewportHeight } = useCardMetrics(cardRef)

  const Hero = TOUR_HEROES[step.target]
  const variants = prefersReducedMotion ? reducedContentVariants : contentVariants

  // Center the card on its target, clamped so it never runs off screen.
  const maxTop = Math.max(VIEWPORT_MARGIN, viewportHeight - cardHeight - VIEWPORT_MARGIN)
  const cardTop =
    anchorY == null
      ? Math.max(VIEWPORT_MARGIN, (viewportHeight - cardHeight) / 2)
      : Math.min(Math.max(anchorY - cardHeight / 2, VIEWPORT_MARGIN), maxTop)

  return (
    <motion.div
      className="pointer-events-none fixed top-0 z-40"
      style={{ width: CARD_WIDTH, [side]: sidepanelVisibleWidth(sidepanelWidth) + TOUR_CARD_PANEL_GAP }}
      animate={{ y: cardTop }}
      initial={false}
      transition={prefersReducedMotion ? { duration: 0 } : { type: 'spring', duration: 0.35, bounce: 0 }}
    >
      <div
        ref={cardRef}
        className="bg-background corner-smooth pointer-events-auto overflow-hidden rounded-2xl shadow-xl backdrop-blur-[4px]"
      >
        <Interactive>
          {/* Keyed, but deliberately not wrapped in AnimatePresence: waiting out
              an exit animation would collapse the card to zero height between
              steps. The new step simply fades in over the old one's footprint. */}
          <motion.div
            key={step.target}
            variants={variants}
            initial="hidden"
            animate="visible"
            transition={contentTransition}
          >
            <Hero />

            <div className="relative flex flex-col gap-2 px-4 pb-4 pt-3">
              <h3 className="text-foreground text-md pr-12 font-semibold">{step.title}</h3>
              <span className="text-foreground-secondary type-ui-xs absolute right-4 top-4 font-medium tabular-nums">
                {stepIndex + 1} of {totalSteps}
              </span>

              {step.paragraphs.map((paragraph) => (
                <p key={paragraph} className="text-foreground type-prose-sm">
                  {renderParagraph(paragraph, step.emphasis)}
                </p>
              ))}

              {/* Skip is the opt-out and sits apart from the pair that walks
                  the tour, so it can't be hit while stepping through. */}
              <div className="flex items-center justify-between gap-2 pt-1">
                {!isLastStep ? (
                  <Button variant="ghost" onClick={onSkip}>
                    Skip
                  </Button>
                ) : (
                  <span />
                )}
                <div className="flex items-center gap-1.5">
                  {!isFirstStep && (
                    <Button variant="outline" onClick={onPrev}>
                      Back
                    </Button>
                  )}
                  <Button variant="primary" onClick={onNext}>
                    {isLastStep ? 'Start using Highlight' : 'Next'}
                  </Button>
                </div>
              </div>
            </div>
          </motion.div>
        </Interactive>
      </div>

      {/* Outside the card's overflow clip so it can reach across the gap. */}
      {anchorY != null && cardHeight > 0 && (
        <TourConnector
          offsetY={Math.min(Math.max(anchorY - cardTop, 0), cardHeight)}
          side={side}
          gap={TOUR_CARD_PANEL_GAP}
        />
      )}
    </motion.div>
  )
}
